import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Gift, Landmark, Lock, QrCode, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, czk, errMsg, media } from "../lib/api";
import { useAuth, useCart, useSite } from "../lib/store";
import { useSeo } from "../components/Seo";

const F = ({ label, testid, ...rest }) => (
  <label className="block text-sm">
    <span className="mb-1 block font-medium">{label}</span>
    <input className="input-soft" data-testid={testid} {...rest} />
  </label>
);

const PayOption = ({ id, cur, set, icon: I, title, sub }) => (
  <button type="button" onClick={() => set(id)} data-testid={`pay-method-${id}`}
    className={`flex w-full items-center gap-4 rounded-3xl border-2 p-4 text-left transition-all ${cur === id ? "border-[#7A8460] bg-[#E9EFE0]" : "border-[#E2DACB] bg-white hover:border-[#A3AC86]"}`}>
    <span className={`grid h-12 w-12 place-items-center rounded-full ${cur === id ? "bg-[#7A8460] text-white" : "bg-[#F7F3EA]"}`}><I className="h-5 w-5" /></span>
    <span><span className="block font-semibold">{title}</span><span className="text-xs text-[#6E7565]">{sub}</span></span>
  </button>
);

export default function Checkout() {
  useSeo({ title: "Pokladna" });
  const { items, clear } = useCart();
  const { user } = useAuth();
  const { site } = useSite();
  const pm = site?.settings?.payments || {};
  const avail = ["stripe", "comgate", "qr"].filter((k) => pm[k]);
  const nav = useNavigate();
  const [b, setB] = useState({ name: "", email: "", street: "", city: "", zip: "", company: "", ico: "", dic: "" });
  const [method, setMethod] = useState("stripe");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState("");
  const [voucherIn, setVoucherIn] = useState("");
  const [voucher, setVoucher] = useState("");
  const [quote, setQuote] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (avail.length && !avail.includes(method)) setMethod(avail[0]);
  }, [avail.join(), method]); // eslint-disable-line
  const [firm, setFirm] = useState(false);
  const [summaryVisible, setSummaryVisible] = useState(false);
  useEffect(() => {
    const f = () => { const el = document.getElementById("checkout-summary"); setSummaryVisible(!!el && el.getBoundingClientRect().top < window.innerHeight - 80); };
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, [quote]);

  useEffect(() => {
    if (user) setB((x) => ({ ...x, name: user.name || "", email: user.email, street: user.street || "", city: user.city || "", zip: user.zip || "", company: user.company || "", ico: user.ico || "", dic: user.dic || "" }));
  }, [user]);
  useEffect(() => {
    if (!items.length) return;
    api.post("/cart/quote", { items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })), coupon: applied || null, voucher: voucher || null })
      .then((r) => {
        setQuote(r.data);
        if (r.data.coupon_error) toast.error(r.data.coupon_error);
        if (r.data.voucher_error) { toast.error(r.data.voucher_error); setVoucher(""); }
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [items, applied, voucher]);

  if (user === null) return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="checkout-login-required">
      <h1 className="h-display text-4xl">Skoro hotovo!</h1>
      <p className="mt-4 text-[#6E7565]">Pro dokončení nákupu se prosím přihlaste nebo zaregistrujte – zakoupené soubory pak najdete ve svém účtu.</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/prihlaseni?next=/pokladna" className="btn-sage" data-testid="checkout-login-btn">Přihlásit se</Link>
        <Link to="/registrace?next=/pokladna" className="btn-ghost" data-testid="checkout-register-btn">Registrovat</Link>
      </div>
    </div>
  );
  if (!items.length) return <div className="py-24 text-center"><p className="h-section text-3xl">Košík je prázdný</p><Link to="/eshop" className="btn-sage mt-6">Do e-shopu</Link></div>;

  const free = quote && quote.items?.length > 0 && quote.total <= 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!free && !avail.includes(method)) return toast.error("Momentálně není dostupná žádná platební metoda");
    if (!agree) return toast.error("Potvrďte prosím souhlas s obchodními podmínkami");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })), coupon: applied || null, voucher: quote?.voucher || null, payment_method: free ? "free" : method, billing: b, origin_url: window.location.origin, agree_terms: agree });
      clear();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else nav(`/objednavka/${data.order_id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-8 lg:grid-cols-[1.4fr_1fr]" data-testid="checkout-page">
      <div className="space-y-8">
        <h1 className="h-display text-4xl sm:text-5xl">Pokladna</h1>
        <section className="card-soft space-y-4 p-6">
          <h2 className="h-section text-xl">Fakturační údaje</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Jméno a příjmení *" required value={b.name} onChange={set("name")} testid="checkout-name" />
            <F label="E-mail *" type="email" required value={b.email} onChange={set("email")} testid="checkout-email" />
            <F label="Ulice a č. p." value={b.street} onChange={set("street")} testid="checkout-street" />
            <div className="grid grid-cols-[1fr_110px] gap-3"><F label="Město" value={b.city} onChange={set("city")} testid="checkout-city" /><F label="PSČ" value={b.zip} onChange={set("zip")} testid="checkout-zip" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={firm} onChange={(e) => setFirm(e.target.checked)} className="accent-[#7A8460]" data-testid="checkout-company-toggle" /> Nakupuji na firmu</label>
          {firm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="grid gap-4 sm:grid-cols-3">
              <F label="Firma" value={b.company} onChange={set("company")} testid="checkout-company" />
              <F label="IČO" value={b.ico} onChange={set("ico")} testid="checkout-ico" />
              <F label="DIČ" value={b.dic} onChange={set("dic")} testid="checkout-dic" />
            </motion.div>
          )}
        </section>
        <section className="card-soft space-y-3 p-6">
          <h2 className="h-section text-xl">Způsob platby</h2>
          {free ? <p className="rounded-2xl bg-[#E9EFE0] p-4 text-sm" data-testid="checkout-paid-by-voucher">{quote.voucher_amount > 0 ? "Objednávka je plně uhrazena dárkovým poukazem – není potřeba nic doplácet." : "Objednávka je zdarma – není potřeba nic platit. Soubory budou ke stažení hned po dokončení."}</p> : <>
          {!avail.length && <p className="rounded-2xl bg-[#FFF4D6] p-4 text-sm" data-testid="checkout-no-payments">Online platby právě připravujeme. Objednávku zatím nelze dokončit – zkuste to prosím později nebo nás kontaktujte.</p>}
          {pm.stripe && <PayOption id="stripe" cur={method} set={setMethod} icon={CreditCard} title="Platební karta online" sub="Okamžité zpřístupnění souborů – zabezpečeno Stripe" />}
          {pm.comgate && <PayOption id="comgate" cur={method} set={setMethod} icon={Landmark} title="Comgate – karta, bankovní tlačítko" sub="Rychlá online platba přes českou bránu Comgate" />}
          {pm.qr && <PayOption id="qr" cur={method} set={setMethod} icon={QrCode} title="Bankovní převod / QR platba" sub="Soubory zpřístupníme po připsání platby a potvrzení obchodníkem" />}
          </>}
          <p className="pt-2 text-xs text-[#6E7565]">Doprava: zdarma – zboží je doručeno elektronicky do vašeho účtu.</p>
        </section>
      </div>
      <aside id="checkout-summary" className="h-fit scroll-mt-24 space-y-4 lg:sticky lg:top-28">
        <div className="card-soft p-6">
          <h2 className="h-section text-xl">Shrnutí objednávky</h2>
          <ul className="mt-4 space-y-3">
            {(quote?.items || []).map((i) => (
              <li key={i.product_id} className="flex items-center gap-3 text-sm">
                <img src={media(i.image)} alt="" className="h-12 w-12 rounded-xl object-cover" />
                <span className="flex-1">{i.name} × {i.qty}</span><span className="font-semibold">{czk(i.price * i.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border bg-[#F7F3EA] px-4"><Tag className="h-4 w-4 text-[#6E7565]" />
              <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Slevový kód" className="w-full bg-transparent py-2.5 text-sm uppercase outline-none" data-testid="checkout-coupon-input" /></div>
            <button type="button" className="btn-ghost" onClick={() => setApplied(coupon.trim())} data-testid="checkout-coupon-apply">Použít</button>
          </div>
          <div className="mt-2 flex gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border bg-[#FDF3D3] px-4"><Gift className="h-4 w-4 text-[#6E7565]" />
              <input value={voucherIn} onChange={(e) => setVoucherIn(e.target.value)} placeholder="Kód poukazu" className="w-full bg-transparent py-2.5 text-sm uppercase outline-none" data-testid="checkout-voucher-input" /></div>
            <button type="button" className="btn-ghost" onClick={() => setVoucher(voucherIn.trim())} data-testid="checkout-voucher-apply">Uplatnit</button>
          </div>
          <div className="mt-5 space-y-1 border-t pt-4 text-sm">
            <div className="flex justify-between"><span>Mezisoučet</span><span>{czk(quote?.subtotal)}</span></div>
            {quote?.discount > 0 && <div className="flex justify-between text-[#D96B43]" data-testid="checkout-discount"><span>Sleva ({quote.coupon})</span><span>-{czk(quote.discount)}</span></div>}
            {quote?.voucher_amount > 0 && <div className="flex justify-between text-[#D96B43]" data-testid="checkout-voucher-line"><span>Dárkový poukaz {quote.voucher}<span className="block text-xs text-[#6E7565]">zůstane {czk(quote.voucher_balance - quote.voucher_amount)}</span></span><span>-{czk(quote.voucher_amount)}</span></div>}
            <div className="flex justify-between"><span>Doprava</span><span>0 Kč</span></div>
            <div className="flex items-center justify-between pt-2"><span className="font-semibold">Celkem</span><span className="h-section text-3xl" data-testid="checkout-total">{czk(quote?.total)}</span></div>
          </div>
          <label className="mt-4 flex items-start gap-2 text-xs text-[#6E7565]">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#7A8460]" data-testid="checkout-terms-checkbox" />
            <span>Souhlasím s <Link to="/obchodni-podminky" target="_blank" className="underline">obchodními podmínkami</Link> a <Link to="/gdpr" target="_blank" className="underline">zpracováním osobních údajů</Link>. Beru na vědomí, že po zpřístupnění digitálního obsahu nelze odstoupit od smlouvy.</span>
          </label>
          <button className="btn-sage mt-5 w-full !py-4" disabled={busy || !quote || (!free && !avail.includes(method))} data-testid="checkout-submit-button"><Lock className="h-4 w-4" /> {busy ? "Zpracovávám…" : free ? (quote.voucher_amount > 0 ? "Dokončit objednávku" : "Získat zdarma") : method === "qr" ? "Objednat s povinností platby" : "Zaplatit online"}</button>
        </div>
      </aside>
      {quote && !summaryVisible && (
        <div className="fixed inset-x-0 bottom-0 z-[45] flex items-center gap-3 border-t bg-[#FFFDF8]/95 px-4 pt-3 backdrop-blur-xl lg:hidden" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="checkout-mobile-bar">
          <div className="flex-1"><p className="text-xs text-[#6E7565]">Celkem k úhradě</p><p className="font-serif text-xl font-black">{czk(quote.total)}</p></div>
          <button type="button" className="btn-sage !py-3.5" onClick={() => document.getElementById("checkout-summary").scrollIntoView({ behavior: "smooth" })} data-testid="checkout-mobile-bar-btn">K dokončení</button>
        </div>
      )}
    </form>
  );
}
